import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cookie configuration
  const cookieOptions = {
    expires: 30, // 30 days
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict'
  };

  useEffect(() => {
    // Try to load user from cookies first, then fallback to localStorage
    const loadUser = () => {
      setLoading(true);
      
      // Check for auth data in cookies
      const userIdCookie = Cookies.get('auth_user_id');
      const emailCookie = Cookies.get('auth_email');
      const tokenCookie = Cookies.get('auth_token');
      
      // If we have cookie data, use it
      if (userIdCookie && emailCookie) {
        const userData = {
          _id: userIdCookie,
          email: emailCookie,
          type: Cookies.get('auth_type') || 'Free',
          username: Cookies.get('auth_username'),
          first_login: Cookies.get('auth_first_login') === 'true',
          token: tokenCookie
        };
        
        setUser(userData);
        
        // Sync to localStorage for compatibility
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('_id', userIdCookie);
        localStorage.setItem('email', emailCookie);
        localStorage.setItem('token', tokenCookie);
        localStorage.setItem('type', userData.type);
        localStorage.setItem('username', userData.username);
        localStorage.setItem('first_login', userData.first_login);
      } 
      // Fallback to localStorage
      else {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          
          // Sync back to cookies
          Cookies.set('auth_user_id', userData._id, cookieOptions);
          Cookies.set('auth_email', userData.email, cookieOptions);
          Cookies.set('auth_token', userData.token || localStorage.getItem('token'), cookieOptions);
          Cookies.set('auth_type', userData.type || localStorage.getItem('type') || 'Free', cookieOptions);
          Cookies.set('auth_username', userData.username || localStorage.getItem('username'), cookieOptions);
          Cookies.set('auth_first_login', (userData.first_login || localStorage.getItem('first_login') === 'true'), cookieOptions);
        }
      }
      
      setLoading(false);
    };
    
    loadUser();
    
    // Set up listener for storage events (for multi-tab support)
    const handleStorageChange = () => {
      loadUser();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const login = (userData, type) => {
    setUser(userData);
    // Store in localStorage
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('_id', userData._id);
    localStorage.setItem('email', userData.email);
    localStorage.setItem('token', userData.token);
    localStorage.setItem('type', type );
    localStorage.setItem('username', userData.username);
    localStorage.setItem('first_login', userData.first_login);
    
    // Store in cookies
    Cookies.set('auth_user_id', userData._id, cookieOptions);
    Cookies.set('auth_email', userData.email, cookieOptions);
    Cookies.set('auth_token', userData.token, cookieOptions);
    Cookies.set('auth_type', type, cookieOptions);
    Cookies.set('auth_username', userData.username, cookieOptions);
    Cookies.set('auth_first_login', userData.first_login, cookieOptions);
  };

  const logout = () => {
    setUser(null);
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('_id');
    localStorage.removeItem('email');
    localStorage.removeItem('token');
    localStorage.removeItem('type');
    localStorage.removeItem('username');
    localStorage.removeItem('first_login');
    
    // Clear cookies
    Cookies.remove('auth_user_id');
    Cookies.remove('auth_email');
    Cookies.remove('auth_token');
    Cookies.remove('auth_type');
    Cookies.remove('auth_username');
    Cookies.remove('auth_first_login');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      isAuthenticated: !!user,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);